import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import TextInputComponent from "../components/ui/TextInputComponent";
import ButtonComponent from "../components/ui/ButtonComponent";
import SelectInputComponent from "../components/ui/SelectInputComponent";
import { Box, Grid, Typography } from "@mui/material";
import { SketchPicker } from "react-color";
import axios from "axios";
import { auth } from "../firebase-config";
import { useToast } from "../components/ui/ToastContext";

// NOTE: leaving these imports even if unused elsewhere in your file
// because you had them before and may be using them in future edits.
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const MicrositeBuilder = () => {
  const { eventId } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    welcomeMessage: "",
    callToAction: "",
    donationHeadline: "",
    secondaryCTA: "",
    primaryColor: "#000000",
    backgroundColor: "#ffffff",
    backgroundImageUrl: "",
    primaryActionType: "",
    textBackgroundColor: "#000000",
    textBackgroundOpacity: 0.5,
    showDonateButton: false,
  });

  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState("");
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [backgroundUploadLoading, setBackgroundUploadLoading] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState("");

  useEffect(() => {
    const loadEventData = async (user) => {
      try {
        const idToken = await user.getIdToken();
        const orgId = sessionStorage.getItem("organizationId");

        const response = await axios.get(
          "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "organization-id": orgId,
            },
            params: { eventId },
          }
        );

        const event = response.data?.eventData;
        if (event) {
          const micrositeConfig = event.micrositeConfig || {};

          setFormData((prev) => ({
            ...prev,
            welcomeMessage:
              micrositeConfig.welcomeMessage ||
              `Welcome to ${event.eventName}`,
            callToAction:
              micrositeConfig.callToAction ||
              inferCallToAction(event.eventType),
            donationHeadline:
              micrositeConfig.donationHeadline ||
              `Support our ${event.eventType?.toLowerCase() || "event"}`,
            eventDescription:
              micrositeConfig.eventDescription || event.description || "",
            backgroundColor: micrositeConfig.backgroundColor || "#ffffff",
            textColor: micrositeConfig.textColor || "#000000",
            backgroundImageUrl: micrositeConfig.backgroundImageUrl || "",
            primaryActionType: micrositeConfig.primaryActionType || "",
            contactPersonName: event.contactPersonName || "",
            contactPersonEmail: event.contactPersonEmail || "",
            contactPersonPhone: event.contactPersonPhone || "",
            eventName: event.eventName || "",
            eventType: event.eventType || "",
            location: event.location || "",
            startDate: event.startDate || "",
            eventTags: event.tags || [],
            textShadingColor: micrositeConfig.textShadingColor || null,
            includeDonateButton: !!micrositeConfig.includeDonateButton,
          }));

          if (micrositeConfig.backgroundImageUrl) {
            setBackgroundPreviewUrl(micrositeConfig.backgroundImageUrl);
          }
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        addToast("Failed to load event data", "error");
      } finally {
        setLoading(false);
      }
    };

    const checkAuthAndFetch = () => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          await loadEventData(user);
        } else {
          addToast("You must be logged in to manage microsites.", "error");
          setLoading(false);
        }
        unsubscribe();
      });
    };

    checkAuthAndFetch();
  }, [eventId]);

  const buildMicrositeConfigPayload = () => ({
    welcomeMessage: formData.welcomeMessage || "",
    callToAction: formData.callToAction || "",
    donationHeadline: formData.donationHeadline || "",
    secondaryCTA: formData.secondaryCTA || "",
    eventDescription: formData.eventDescription || "",
    backgroundColor: formData.backgroundColor || "#ffffff",
    textColor: formData.textColor || "#000000",
    backgroundImageUrl: formData.backgroundImageUrl || "",
    primaryActionType: formData.primaryActionType || "",
    includeDonateButton: !!formData.includeDonateButton,
    textShadingColor: formData.textShadingColor
      ? {
          r: formData.textShadingColor.r ?? 0,
          g: formData.textShadingColor.g ?? 0,
          b: formData.textShadingColor.b ?? 0,
          a: formData.textShadingColor.a ?? 0,
        }
      : null,
  });

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      let updatedForm = { ...prev, [field]: value };

      if (field === "primaryActionType" && !prev.callToAction) {
        if (value === "rsvp") updatedForm.callToAction = "RSVP Today";
        else if (value === "donation")
          updatedForm.callToAction = "Donate Now";
        else if (value === "ticket")
          updatedForm.callToAction = "Buy Tickets";
      }

      return updatedForm;
    });
  };

  const inferCallToAction = (eventType) => {
    if (!eventType) return "Get Involved";
    const lowerType = eventType.toLowerCase();
    if (lowerType.includes("fundraiser")) return "Donate Now";
    if (lowerType.includes("peer")) return "Pledge Support";
    if (lowerType.includes("ticket")) return "Buy Tickets";
    return "RSVP Today";
  };

  const handleBackgroundUpload = async (file) => {
    if (!file) return;

    setBackgroundUploadLoading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const b64 = reader.result.split(",")[1];
          resolve(b64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setBackgroundPreviewUrl(URL.createObjectURL(file));
      setBackgroundFile(file);

      setFormData((prev) => ({
        ...prev,
        backgroundFileBase64: base64,
        backgroundFileName: file.name,
        backgroundMimeType: file.type || "image/jpeg",
        backgroundImageUrl: "",
      }));

      addToast("Background ready for upload!", "success");
    } catch (error) {
      console.error("Background upload failed:", error);
      addToast("Failed to upload background image", "error");
    } finally {
      setBackgroundUploadLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const organizationId = sessionStorage.getItem("organizationId");

      const payload = {
        eventId,
        micrositeConfig: buildMicrositeConfigPayload(),
        ...(formData.backgroundFileBase64 && {
          backgroundFileBase64: formData.backgroundFileBase64,
          backgroundFileName: formData.backgroundFileName,
          backgroundMimeType: formData.backgroundMimeType,
        }),
      };

      const saveRes = await axios.post(
        "https://savemicrositeconfig-774073159618.us-central1.run.app",
        payload,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
        }
      );

      if (saveRes.data?.backgroundImageUrl) {
        setFormData((prev) => ({
          ...prev,
          backgroundImageUrl: saveRes.data.backgroundImageUrl,
          backgroundFileBase64: undefined,
          backgroundFileName: undefined,
          backgroundMimeType: undefined,
        }));
        setBackgroundPreviewUrl(saveRes.data.backgroundImageUrl);
        setBackgroundFile(null);
      }

      addToast("Microsite configuration saved!", "success");
    } catch (error) {
      console.error("Save microsite failed:", error);
      addToast("Failed to save microsite.", "error");
      setBackgroundUploadLoading(false);
    }
  };

  const handleColorChangeDirect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePreview = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const organizationId = sessionStorage.getItem("organizationId");

      const saveRes = await axios.post(
        "https://savemicrositeconfig-774073159618.us-central1.run.app",
        {
          eventId,
          micrositeConfig: buildMicrositeConfigPayload(),
          ...(formData.backgroundFileBase64 && {
            backgroundFileBase64: formData.backgroundFileBase64,
            backgroundFileName: formData.backgroundFileName,
            backgroundMimeType: formData.backgroundMimeType,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
        }
      );

      if (saveRes.data?.backgroundImageUrl) {
        setFormData((prev) => ({
          ...prev,
          backgroundImageUrl: saveRes.data.backgroundImageUrl,
          backgroundFileBase64: undefined,
          backgroundFileName: undefined,
          backgroundMimeType: undefined,
        }));
        setBackgroundPreviewUrl(saveRes.data.backgroundImageUrl);
        setBackgroundFile(null);
      }

      const res = await axios.get(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
          params: { eventId },
        }
      );

      const event = res.data?.eventData;
      if (!event) {
        addToast("Failed to load event data for preview", "error");
        return;
      }

      const response = await axios.post(
        "https://generatemicrositepreview-774073159618.us-central1.run.app",
        {
          eventId,
          organizationId,
          eventData: event,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
        }
      );

      const previewUrl = response.data.previewUrl;
      if (previewUrl) {
        window.open(previewUrl, "_blank");
        addToast("Microsite preview opened in a new tab.", "success");
      } else {
        addToast("Failed to generate preview link.", "error");
      }
    } catch (error) {
      console.error("Preview error:", error);
      addToast("Failed to open microsite preview.", "error");
    }
  };

  // Keep your override domain if you want, but default to current host.
  const LIVE_DOMAIN =
    process.env.REACT_APP_LIVE_MICROSITE_DOMAIN ||
    "https://app.kinovacrm.com";

  const handlePublish = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        addToast("You must be logged in to publish.", "error");
        return;
      }

      const idToken = await user.getIdToken(true);
      const organizationId = sessionStorage.getItem("organizationId");

      if (!organizationId) {
        addToast(
          "Organization ID not found. Please refresh and try again.",
          "error"
        );
        return;
      }

      const saveRes = await axios.post(
        "https://savemicrositeconfig-774073159618.us-central1.run.app",
        {
          eventId,
          micrositeConfig: buildMicrositeConfigPayload(),
          ...(formData.backgroundFileBase64 && {
            backgroundFileBase64: formData.backgroundFileBase64,
            backgroundFileName: formData.backgroundFileName,
            backgroundMimeType: formData.backgroundMimeType,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
        }
      );

      const finalBackgroundUrl =
        saveRes.data?.backgroundImageUrl ||
        formData.backgroundImageUrl ||
        "";

      if (saveRes.data?.backgroundImageUrl) {
        setFormData((prev) => ({
          ...prev,
          backgroundImageUrl: saveRes.data.backgroundImageUrl,
          backgroundFileBase64: undefined,
          backgroundFileName: undefined,
          backgroundMimeType: undefined,
        }));
        setBackgroundPreviewUrl(saveRes.data.backgroundImageUrl);
        setBackgroundFile(null);
      }

      const publishPayload = {
        eventId,
        visibleToPublic: true,
        allowRsvps: formData.primaryActionType === "rsvp",
        micrositeConfig: {
          ...buildMicrositeConfigPayload(),
          backgroundImageUrl: finalBackgroundUrl,
        },
      };

      await axios.put(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/updateEvent",
        publishPayload,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": organizationId,
          },
        }
      );

      // Prefer current host for safety; fall back to configured LIVE_DOMAIN
      const host = window.location?.origin || LIVE_DOMAIN;
      const liveUrl = `${host}/${organizationId}/${eventId}`;

      setPublishedUrl(liveUrl);
      setPublishModalOpen(true);

      addToast("Microsite published! Event is now public.", "success");
    } catch (error) {
      console.error("Publish Microsite error:", error);
      addToast("Failed to publish microsite.", "error");
    }
  };

  if (loading) {
    return (
      <div className="manage-users-container">
        <Sidebar />
        <div className="manage-users-content">
          <PageHeader title="Microsite Builder" />
          <Box p={3}>Loading…</Box>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader title="Microsite Builder" />

        <Box mt={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box
                p={0}
                border="1px solid #ccc"
                borderRadius={2}
                overflow="hidden"
                bgcolor={
                  backgroundPreviewUrl
                    ? "transparent"
                    : formData.backgroundColor || "#f9f9f9"
                }
                position="relative"
                height="500px"
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="center"
                sx={{
                  backgroundImage: backgroundPreviewUrl
                    ? `url(${backgroundPreviewUrl})`
                    : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "background 0.5s ease",
                }}
              >
                <Box
                  p={3}
                  textAlign="center"
                  color={formData.textColor || "#000"}
                  sx={{
                    backgroundColor: formData.textShadingColor
                      ? `rgba(${formData.textShadingColor.r},${formData.textShadingColor.g},${formData.textShadingColor.b},${formData.textShadingColor.a})`
                      : "transparent",
                    width: "100%",
                    transition: "background-color 0.5s ease",
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    className="preview-text-fade"
                  >
                    {formData.welcomeMessage}
                  </Typography>

                  {formData.donationHeadline && (
                    <Typography
                      variant="h6"
                      gutterBottom
                      className="preview-text-fade"
                    >
                      {formData.donationHeadline}
                    </Typography>
                  )}

                  {formData.callToAction && (
                    <ButtonComponent
                      label={formData.callToAction}
                      type="primary"
                      onClick={() => {}}
                      style={{ marginTop: "1rem" }}
                      className="preview-text-fade"
                    />
                  )}

                  {formData.includeDonateButton && (
                    <ButtonComponent
                      label="Donate Now"
                      type="secondary"
                      onClick={() => {}}
                      style={{ marginTop: "0.75rem" }}
                      className="preview-text-fade"
                    />
                  )}

                  {formData.secondaryCTA && (
                    <Typography
                      variant="body1"
                      sx={{ mt: 2 }}
                      className="preview-text-fade"
                    >
                      {formData.secondaryCTA}
                    </Typography>
                  )}

                  {formData.eventDescription && (
                    <Box mt={3}>
                      <Typography
                        variant="subtitle1"
                        className="preview-text-fade"
                      >
                        About the Event
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1 }}
                        className="preview-text-fade"
                      >
                        {formData.eventDescription}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Customize Your Microsite
              </Typography>

              <TextInputComponent
                label="Welcome Message"
                value={formData.welcomeMessage}
                onChange={handleChange("welcomeMessage")}
              />
              <TextInputComponent
                label="Main Call-To-Action"
                value={formData.callToAction}
                onChange={handleChange("callToAction")}
              />
              <TextInputComponent
                label="Donation Headline"
                value={formData.donationHeadline}
                onChange={handleChange("donationHeadline")}
              />

              <Box mt={1} display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="includeDonateButton"
                  checked={formData.includeDonateButton || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      includeDonateButton: e.target.checked,
                    }))
                  }
                />
                <label
                  htmlFor="includeDonateButton"
                  style={{ marginLeft: 8 }}
                >
                  Include Donate Button
                </label>
              </Box>

              <SelectInputComponent
                label="Primary Action Type"
                value={formData.primaryActionType}
                onChange={handleChange("primaryActionType")}
                options={[
                  { value: "", label: "Select an Action Type" },
                  { value: "rsvp", label: "RSVP" },
                  { value: "donation", label: "Donation" },
                  { value: "ticket", label: "Ticket Sale" },
                ]}
              />

              <TextInputComponent
                label="Secondary Call-To-Action"
                value={formData.secondaryCTA}
                onChange={handleChange("secondaryCTA")}
              />
              <TextInputComponent
                label="Event Description"
                multiline
                rows={4}
                value={formData.eventDescription}
                onChange={handleChange("eventDescription")}
              />

              <Box mt={4}>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      Background Color
                    </Typography>
                    <Box sx={{ width: 220, margin: "0 auto" }}>
                      <SketchPicker
                        color={formData.backgroundColor}
                        onChange={(color) =>
                          handleColorChangeDirect(
                            "backgroundColor",
                            color.hex
                          )
                        }
                        disableAlpha
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      Text Color
                    </Typography>
                    <Box sx={{ width: 220, margin: "0 auto" }}>
                      <SketchPicker
                        color={formData.textColor}
                        onChange={(color) =>
                          handleColorChangeDirect("textColor", color.hex)
                        }
                        disableAlpha
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      Text Background Shading
                    </Typography>
                    <Box sx={{ width: 220, margin: "0 auto" }}>
                      <SketchPicker
                        color={
                          formData.textShadingColor || {
                            r: 0,
                            g: 0,
                            b: 0,
                            a: 0,
                          }
                        }
                        onChange={(color) => {
                          setFormData((prev) => ({
                            ...prev,
                            textShadingColor: color.rgb,
                          }));
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mt={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="background-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleBackgroundUpload(file);
                    }}
                  />
                  <label htmlFor="background-upload">
                    <ButtonComponent
                      label={
                        backgroundUploadLoading
                          ? "Uploading..."
                          : "Choose Background Image"
                      }
                      type="secondary"
                      loading={backgroundUploadLoading}
                      disabled={backgroundUploadLoading}
                      onClick={() => {
                        document.getElementById("background-upload").click();
                      }}
                    />
                  </label>

                  {backgroundUploadLoading && (
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      Uploading background image...
                    </Typography>
                  )}

                  {!backgroundUploadLoading && backgroundPreviewUrl && (
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Selected:{" "}
                      {backgroundFile?.name || "Background Selected"}
                    </Typography>
                  )}
                </Box>

                {backgroundPreviewUrl && (
                  <Box
                    mt={2}
                    sx={{
                      opacity: backgroundUploadLoading ? 0.5 : 1,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <img
                      src={backgroundPreviewUrl}
                      alt="Background Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "150px",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box mt={4} display="flex" gap={2}>
                <ButtonComponent
                  label="Save Changes"
                  type="primary"
                  onClick={handleSave}
                  disabled={backgroundUploadLoading}
                />
                <ButtonComponent
                  label="Preview Microsite"
                  variant="outlined"
                  onClick={handlePreview}
                  disabled={backgroundUploadLoading}
                />
                <ButtonComponent
                  label="Publish Microsite"
                  type="secondary"
                  onClick={handlePublish}
                  disabled={backgroundUploadLoading}
                />
              </Box>

              {backgroundUploadLoading && (
                <Typography variant="body2" color="textSecondary" mt={2}>
                  Uploading background image...
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {publishModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "white",
                padding: "2rem",
                borderRadius: "8px",
                textAlign: "center",
                maxWidth: "400px",
                width: "90%",
              }}
            >
              <h2 style={{ marginBottom: "1rem" }}>Microsite Published!</h2>
              <p style={{ marginBottom: "1rem" }}>
                Your microsite is now live:
              </p>
              <a
                href={publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ wordBreak: "break-word" }}
              >
                {publishedUrl}
              </a>
              <Box mt={3} display="flex" justifyContent="center" gap={2}>
                <ButtonComponent
                  label="Copy Link"
                  type="primary"
                  onClick={() => {
                    navigator.clipboard.writeText(publishedUrl);
                    addToast("Link copied to clipboard!", "success");
                  }}
                />
                <ButtonComponent
                  label="Close"
                  type="secondary"
                  onClick={() => setPublishModalOpen(false)}
                />
              </Box>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MicrositeBuilder;